import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { SessionUser } from "@mtform/shared";
import { ApiError, api, setUnauthorizedHandler } from "@/lib/api";

// =============================================================================
// AuthContext
//
// Single source of truth for "is the current browser signed in, and as
// whom". Boots by calling /api/auth/me; dependents (TripForm,
// AccountMenu, ProtectedRoute) re-render when the user changes.
//
// Three states:
//   loading        — initial /me hasn't returned yet
//   authenticated  — /me said yes, user record is loaded
//   unauthenticated — /me said no, or the request errored
// =============================================================================

export type AuthState =
  | { status: "loading" }
  | { status: "authenticated"; user: SessionUser }
  | { status: "unauthenticated" };

interface AuthContextValue {
  state: AuthState;
  /** True when the session was lost mid-use (a 401 on a call after we'd been
   *  authenticated), as opposed to simply never having signed in. Drives the
   *  "your session expired" notice on the login page. */
  sessionExpired: boolean;
  /** Re-fetch /me. Call after a successful login/logout to refresh state. */
  refresh: () => Promise<void>;
  /** Apply a freshly-issued session locally without a roundtrip. Each
   *  auth endpoint (login, magic-link consume, invitation accept) returns
   *  the SessionUser inline, so the page can hand it off without forcing
   *  another /me. */
  hydrate: (user: SessionUser) => void;
  /** Hit POST /api/auth/logout and reset state. Use from the avatar menu. */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Pauses before asking /me again when the server couldn't answer. */
const ME_RETRY_DELAYS_MS = [1_000, 2_000, 4_000];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  const [sessionExpired, setSessionExpired] = useState(false);

  // Mirror of "are we currently signed in", read by the global 401 handler —
  // it's registered once, so it can't close over the live `state`.
  const authedRef = useRef(false);
  useEffect(() => {
    authedRef.current = state.status === "authenticated";
  }, [state.status]);

  // Bumped by every sign-in, sign-out and expiry. A /me answer that comes
  // back after one of those is about the old session and is dropped, so a
  // late or retried /me can't undo a sign-in or put a signed-out user back.
  const sessionEpoch = useRef(0);

  // React to the session going invalid on ANY call — a tab left open past the
  // cookie's expiry, a revoked session, etc. The api layer fires this on a 401
  // UNAUTHENTICATED. We act ONLY when we currently think we're signed in, which:
  //   - skips the normal logged-out states (bootstrap /me, the login page) so
  //     this can never loop, and
  //   - debounces the burst of 401s a reconnect/refetch fires — the first flips
  //     us to unauthenticated; the rest are no-ops.
  // Flipping to `unauthenticated` makes ProtectedRoute redirect to /login, with
  // `sessionExpired` driving the "your session expired" notice there.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (!authedRef.current) return;
      authedRef.current = false;
      sessionEpoch.current++;
      setSessionExpired(true);
      setState({ status: "unauthenticated" });
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const fetchMe = useCallback(async (signal?: AbortSignal) => {
    const epoch = sessionEpoch.current;
    const stale = () => signal?.aborted || sessionEpoch.current !== epoch;
    const started = Date.now();
    for (let attempt = 0; ; attempt++) {
      try {
        const res = await api.auth.me(signal);
        if (stale()) return;
        if (res.authenticated) {
          setState({ status: "authenticated", user: res.user });
        } else {
          setState({ status: "unauthenticated" });
        }
        return;
      } catch (err) {
        if (stale()) return;
        if (err instanceof ApiError && err.code === "UNAUTHENTICATED") {
          setState({ status: "unauthenticated" });
          return;
        }
        // The server couldn't answer (restarting, database briefly
        // unreachable, no network): ask again shortly, rather than show the
        // sign-in page to someone whose session is fine. Only for a blip:
        // not once 10 s have gone by (a hard outage answers slowly).
        const delay = ME_RETRY_DELAYS_MS[attempt];
        if (
          delay !== undefined &&
          Date.now() - started < 10_000 &&
          err instanceof ApiError &&
          (err.status >= 500 || err.code === "NETWORK_ERROR")
        ) {
          await new Promise((resolve) => setTimeout(resolve, delay));
          if (stale()) return;
          continue;
        }
        console.warn("[auth] /me failed:", err);
        // Someone already signed in (a refresh after saving their profile)
        // stays signed in. Otherwise the sign-in page, so they can retry
        // rather than be stuck on a spinner.
        setState((s) => (s.status === "authenticated" ? s : { status: "unauthenticated" }));
        return;
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void fetchMe(controller.signal);
    return () => controller.abort();
  }, [fetchMe]);

  const hydrate = useCallback((user: SessionUser) => {
    sessionEpoch.current++;
    setSessionExpired(false);
    setState({ status: "authenticated", user });
  }, []);

  const signOut = useCallback(async () => {
    sessionEpoch.current++;
    try {
      await api.auth.logout();
    } catch (err) {
      console.warn("[auth] logout failed:", err);
    }
    // Again: a refresh started while the logout was on its way is about the
    // session being ended.
    sessionEpoch.current++;
    setState({ status: "unauthenticated" });
  }, []);

  // Memoize the context value: a fresh object every render would invalidate
  // every consumer's `useEffect([auth])` deps, which is exactly what made
  // /auth/magic re-fire `consumeMagicLink` after `hydrate()` flipped the
  // state. fetchMe/hydrate/signOut are already stable via useCallback, so
  // `state` is the only dep that should bust the value identity.
  const refresh = useCallback(() => fetchMe(), [fetchMe]);
  const value = useMemo<AuthContextValue>(
    () => ({ state, sessionExpired, refresh, hydrate, signOut }),
    [state, sessionExpired, refresh, hydrate, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }
  return ctx;
}
